import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BottomSheetContentLayout, Box, GridItem, MenuItem, Select, TextButton, TextField, useDevice, useOverlayContext, WithInfoIcon } from 'seagull';
import { getCustomMessageValidation, validateViaRHF } from '~/business-logic/validators';
import { useLocaleKeys } from '~/i18n';
import { useStoreActions } from '~/store/store-context';
import { connectControllerToTextField, RHF_VARIABLE_PICKER_SET_VALUE_OPTIONS } from '~/utils/react-hook-form.utils';
import { useTextFieldVariableIntegration } from '../../TemplateVariables/hooks/useTextFieldVariableIntegration';
import { VariableFieldAdornment } from '../../TemplateVariables/VariableFieldAdornment/VariableFieldAdornment';
import styles from './IntroMessage.module.scss';
import { isCustomMessageType, orderedTemplateTypesForDropdown, templateTypeToDropdownLabel, useEditIntroMessage } from './IntroMessage.utils';
const messageRules = getCustomMessageValidation('Introductory message', true);
export function validateIntroMessage(value) {
    return validateViaRHF(messageRules, value);
}
function EditMessageInBottomSheet(props) {
    const { t: introTexts } = useLocaleKeys('agreementStudio.introMessage');
    const { headerTitle, message, onChange } = props;
    const { control, getValues } = useForm({
        mode: 'all',
        defaultValues: {
            message
        }
    });
    const textFieldProps = {
        collapseHelperText: false,
        testId: 'template-content',
        placeholder: introTexts.placeholder(),
        multiline: true,
        minRows: 9,
        maxRows: 9
    };
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        header: {
            title: headerTitle
        },
        footer: {
            primaryAction: {
                bi: {
                    name: 'save_intro_message'
                },
                text: introTexts.saveButton(),
                onClick: ()=>onChange(getValues('message'))
            }
        },
        children: /*#__PURE__*/ _jsx(Box, {
            padding: 8,
            children: /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "message",
                rules: messageRules,
                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                        mobile: {
                            renderAsCard: true
                        }
                    }, connectControllerToTextField(renderProps), textFieldProps), {
                        bi: {
                            name: 'intro_message'
                        }
                    }))
            })
        })
    });
}
export default function IntroMessage(props) {
    const { t: introMessageTexts } = useLocaleKeys('components.agreementStudio.introMessage');
    const { t: introTexts } = useLocaleKeys('agreementStudio.introMessage');
    const textFieldProps = {
        collapseHelperText: false,
        testId: 'template-content',
        placeholder: introTexts.placeholder(),
        multiline: true,
        minRows: 9,
        maxRows: 9
    };
    const { initialTemplateContent, disabled, onMessageChange, inputWidth = '512px', dataAgentTarget } = props;
    const { getTemplateTypeByMessage, templateTypeToContent } = useEditIntroMessage();
    const { logBI } = useStoreActions();
    const { isMobileDevice } = useDevice();
    const { pushBottomSheetPage, popBottomSheetPage } = useOverlayContext();
    const defaultValues = {
        type: getTemplateTypeByMessage(initialTemplateContent),
        message: initialTemplateContent
    };
    const [customMessage, setCustomMessage] = useState(isCustomMessageType(defaultValues.type) ? initialTemplateContent : '');
    const { control, setValue, getValues } = useForm({
        mode: 'all',
        defaultValues
    });
    const [showTemplateMessage, setShowTemplateMessage] = useState(false);
    const inputRef = useRef(null);
    const onTypeChange = (newType)=>{
        logBI(10491, {
            templateType: newType
        });
        const newMessage = isCustomMessageType(newType) ? customMessage : templateTypeToContent[newType];
        if (isCustomMessageType(newType)) setShowTemplateMessage(true);
        setValues({
            newType,
            newMessage
        });
    };
    const onEditMessage = (newMessage)=>{
        const newType = getTemplateTypeByMessage(newMessage);
        if (isCustomMessageType(newType)) setCustomMessage(newMessage);
        setValues({
            newType,
            newMessage
        });
    };
    const setValues = (param)=>{
        let { newType, newMessage } = param;
        const [type, message] = getValues([
            'type',
            'message'
        ]);
        if (newMessage !== type) {
            setValue('type', newType, RHF_VARIABLE_PICKER_SET_VALUE_OPTIONS);
        }
        if (newMessage !== message) {
            onMessageChange(newMessage);
            setValue('message', newMessage, RHF_VARIABLE_PICKER_SET_VALUE_OPTIONS);
        }
    };
    const setMessageField = useCallback((newValue)=>{
        onEditMessage(newValue);
    }, [
        onEditMessage
    ]);
    const { enabled, inputProps, adornmentRef, adornmentStyle, fieldAdornmentProps, resolvedValues: _resolvedValues, popovers } = useTextFieldVariableIntegration(inputRef, setMessageField, {
        hideAdornment: disabled,
        adornmentStyle: {
            alignSelf: 'flex-start',
            marginTop: -8
        }
    });
    useEffect(()=>{
        if (!isMobileDevice) return;
        if (showTemplateMessage) {
            pushBottomSheetPage({
                onClose: ()=>setShowTemplateMessage(false),
                content: /*#__PURE__*/ _jsx(EditMessageInBottomSheet, {
                    headerTitle: templateTypeToDropdownLabel[getValues('type')],
                    message: getValues('message'),
                    onChange: (message)=>{
                        onEditMessage(message);
                        setShowTemplateMessage(false);
                    }
                })
            });
        } else {
            popBottomSheetPage();
        }
    }, [
        showTemplateMessage
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(GridItem, {
                span: 12,
                className: styles.firstRow,
                testId: "intro-message",
                children: [
                    /*#__PURE__*/ _jsx(WithInfoIcon, {
                        className: styles.inputContainer,
                        info: introMessageTexts.infoText(),
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "type",
                            render: (renderProps)=>/*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                                    collapseHelperText: false,
                                    mobile: {
                                        renderAsCard: true
                                    }
                                }, connectControllerToTextField(renderProps)), {
                                    onChange: (e)=>{
                                        onTypeChange(e.target.value);
                                        renderProps.field.onChange(e);
                                    },
                                    testId: "template-type-dropdown",
                                    bi: {
                                        name: 'intro_message_type'
                                    },
                                    label: introMessageTexts.label(),
                                    disabled: disabled,
                                    style: {
                                        width: inputWidth
                                    },
                                    "data-agent-target": dataAgentTarget,
                                    children: orderedTemplateTypesForDropdown.map((templateType)=>/*#__PURE__*/ _jsx(MenuItem, {
                                            value: templateType,
                                            testId: "template-type-".concat(templateType),
                                            children: templateTypeToDropdownLabel[templateType]
                                        }, templateType))
                                }))
                        })
                    }),
                    /*#__PURE__*/ _jsx(TextButton, {
                        testId: "edit-message",
                        bi: {
                            name: 'edit_intro_message'
                        },
                        disabled: disabled,
                        onClick: ()=>{
                            logBI(10490);
                            setShowTemplateMessage(true);
                        },
                        style: {
                            visibility: isMobileDevice || !showTemplateMessage ? 'visible' : 'hidden'
                        },
                        children: introMessageTexts.editMessage()
                    })
                ]
            }),
            showTemplateMessage && !isMobileDevice && /*#__PURE__*/ _jsx(GridItem, {
                span: 12,
                children: /*#__PURE__*/ _jsx(Controller, {
                    control: control,
                    name: "message",
                    rules: messageRules,
                    render: (renderProps)=>{
                        const connected = connectControllerToTextField(renderProps);
                        const rhfRef = connected.inputRef;
                        return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                            mobile: {
                                renderAsCard: true
                            }
                        }, connected, textFieldProps), {
                            bi: {
                                name: 'intro_message'
                            },
                            inputRef: (el)=>{
                                inputRef.current = el;
                                if (typeof rhfRef === 'function') {
                                    rhfRef(el);
                                }
                            },
                            inputProps: inputProps,
                            onChange: (e)=>{
                                onEditMessage(e.target.value);
                                renderProps.field.onChange(e);
                            },
                            disabled: disabled,
                            InputProps: enabled ? {
                                endAdornment: /*#__PURE__*/ _jsx("span", {
                                    ref: adornmentRef,
                                    style: adornmentStyle,
                                    children: /*#__PURE__*/ _jsx(VariableFieldAdornment, _object_spread({}, fieldAdornmentProps))
                                })
                            } : undefined
                        }));
                    }
                })
            }),
            popovers
        ]
    });
}
